import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useRef } from 'react';
import { cn } from './cn.js';
/**
 * Copy every `<thead>` header onto the `<td>`s below it as `data-label`, so the
 * card-reflow CSS in tokens.css can print "Customer" beside the customer cell on
 * a phone.
 *
 * Column index is walked with `colSpan`, not cell position: an empty-state row
 * (`<td colSpan={6}>No jobs yet</td>`) otherwise consumes one slot and shifts
 * every later column's label by five. A cell that spans more than one column is
 * not one column's value, so it gets no label at all.
 */
function stampLabels(root) {
    for (const table of root.querySelectorAll('table')) {
        const headRow = table.tHead?.rows[0];
        if (!headRow)
            continue;
        // One entry per COLUMN, not per <th>: a spanning header labels only the
        // first column it covers.
        const labels = [];
        for (const th of headRow.cells) {
            const text = th.textContent?.trim() ?? '';
            for (let i = 0; i < (th.colSpan || 1); i += 1)
                labels.push(i === 0 ? text : '');
        }
        for (const body of table.tBodies) {
            for (const row of body.rows) {
                let column = 0;
                for (const cell of row.cells) {
                    const span = cell.colSpan || 1;
                    // A caller-authored data-label always wins; a spanning cell gets none.
                    if (span === 1 && !cell.hasAttribute('data-label')) {
                        cell.setAttribute('data-label', labels[column] ?? '');
                    }
                    column += span;
                }
            }
        }
    }
}
/**
 * Wrapper that lets a plain semantic `<table>` reflow into stacked label:value
 * cards below the `md` breakpoint (see the `.qelm-responsive-table` block at the
 * end of tokens.css). Desktop is untouched — it stays the same horizontal
 * scroller `Table` already renders.
 *
 * The labels are DERIVED from the table's own `<thead>` at runtime rather than
 * authored per cell. Every table in the two SPAs is hand-rolled JSX with no
 * column definitions to read, so the alternative is hand-adding a `data-label`
 * to several hundred `<td>`s and keeping them in sync with the headers forever.
 * Deriving them keeps the per-call-site change to swapping one wrapper element,
 * and a header rename can never drift from its cells.
 *
 * A `MutationObserver` re-stamps after filtering, sorting, pagination, or any
 * other re-render swaps the rows out.
 *
 * A table with no `<thead>` is left alone: it still stacks into cards, the cells
 * just render without labels.
 */
export function ResponsiveTable({ className, children, ...props }) {
    const ref = useRef(null);
    useEffect(() => {
        const root = ref.current;
        if (!root)
            return;
        stampLabels(root);
        // childList + subtree only. Watching attributes would re-trigger on the
        // data-label writes above and spin.
        const observer = new MutationObserver(() => stampLabels(root));
        observer.observe(root, { childList: true, subtree: true });
        return () => observer.disconnect();
    }, []);
    return (_jsx("div", { ref: ref, className: cn('qelm-responsive-table w-full overflow-x-auto', className), ...props, children: children }));
}
